import type { InfiniteData } from '@tanstack/react-query';

import type {
    AgentChatMessage,
    AgentChatMessagePage,
    AssistantMessageEntry,
    AssistantMessageStatus,
    ContextRefreshEvent,
    StructuredResponseEvent,
} from '../data-contract/chat';

export type MessagesInfiniteData = InfiniteData<AgentChatMessagePage, string | undefined>;

export const seedEmptyMessagesData = (): MessagesInfiniteData => ({
    pageParams: [undefined],
    pages: [{ messages: [], nextCursor: null }],
});

export const getStructuredEventBlockIds = (event: StructuredResponseEvent): readonly string[] =>
    event.payload.blocks.map((block) => block.blockId);

const getReplaceableStructuredEventBlockIds = (event: StructuredResponseEvent): readonly string[] =>
    event.payload.blocks.flatMap((block) =>
        block.type === 'activity-timeline' || block.type === 'status-update' ? [] : [block.blockId],
    );

const updateFirstPage = (
    data: MessagesInfiniteData | undefined,
    update: (messages: readonly AgentChatMessage[]) => readonly AgentChatMessage[],
): MessagesInfiniteData | undefined => {
    if (data === undefined) return data;
    const [firstPage, ...restPages] = data.pages;
    if (firstPage === undefined) return data;
    return { ...data, pages: [{ ...firstPage, messages: [...update(firstPage.messages)] }, ...restPages] };
};

const mapAllMessages = (
    data: MessagesInfiniteData | undefined,
    mapMessage: (message: AgentChatMessage) => AgentChatMessage,
): MessagesInfiniteData | undefined => {
    if (data === undefined) return data;
    return { ...data, pages: data.pages.map((page) => ({ ...page, messages: page.messages.map(mapMessage) })) };
};

export const prependUserMessage = (
    data: MessagesInfiniteData | undefined,
    message: AgentChatMessage,
): MessagesInfiniteData | undefined => updateFirstPage(data, (messages) => [message, ...messages]);

export const upsertAssistantMessage = (
    data: MessagesInfiniteData | undefined,
    messageId: string,
    createdAt: string,
): MessagesInfiniteData | undefined =>
    updateFirstPage(data, (messages) => {
        if (messages.some((message) => message.id === messageId)) {
            return messages.map((message) =>
                message.id === messageId ? { ...message, entries: [], status: 'pending' } : message,
            );
        }
        return [{ createdAt, entries: [], id: messageId, role: 'assistant', status: 'pending' }, ...messages];
    });

const appendOrMergeText = (entries: readonly AssistantMessageEntry[], text: string): AssistantMessageEntry[] => {
    const last = entries[entries.length - 1];
    if (last?.type === 'text') {
        return [...entries.slice(0, -1), { text: last.text + text, type: 'text' }];
    }
    return [...entries, { text, type: 'text' }];
};

export const appendTextChunk = (
    data: MessagesInfiniteData | undefined,
    messageId: string,
    text: string,
): MessagesInfiniteData | undefined =>
    mapAllMessages(data, (message) => {
        if (message.id !== messageId || message.role !== 'assistant') return message;
        return { ...message, entries: appendOrMergeText(message.entries ?? [], text) };
    });

const entryHasOverlappingBlockId = (entry: AssistantMessageEntry, blockIds: ReadonlySet<string>): boolean =>
    entry.type === 'event' && getReplaceableStructuredEventBlockIds(entry.envelope).some((id) => blockIds.has(id));

export const appendStructuredEvent = (
    data: MessagesInfiniteData | undefined,
    messageId: string,
    event: StructuredResponseEvent,
): MessagesInfiniteData | undefined => {
    const incomingBlockIds = new Set(getReplaceableStructuredEventBlockIds(event));
    const incoming: AssistantMessageEntry = { envelope: event, type: 'event' };
    return mapAllMessages(data, (message) => {
        if (message.role !== 'assistant') return message;
        const entries = message.entries ?? [];
        const hasDuplicate =
            incomingBlockIds.size > 0 && entries.some((entry) => entryHasOverlappingBlockId(entry, incomingBlockIds));
        if (message.id !== messageId) {
            if (!hasDuplicate) return message;
            return {
                ...message,
                entries: entries.filter((entry) => !entryHasOverlappingBlockId(entry, incomingBlockIds)),
            };
        }
        const kept = hasDuplicate
            ? entries.filter((entry) => !entryHasOverlappingBlockId(entry, incomingBlockIds))
            : entries;
        return { ...message, entries: [...kept, incoming] };
    });
};

export const appendContextRefreshEvent = (
    data: MessagesInfiniteData | undefined,
    messageId: string,
    event: ContextRefreshEvent,
): MessagesInfiniteData | undefined =>
    mapAllMessages(data, (message) => {
        if (message.role !== 'assistant' || message.id !== messageId) return message;
        return { ...message, entries: [...(message.entries ?? []), { envelope: event, type: 'context-refresh' }] };
    });

export const setAssistantMessageStatus = (
    data: MessagesInfiniteData | undefined,
    messageId: string,
    status: AssistantMessageStatus,
): MessagesInfiniteData | undefined =>
    mapAllMessages(data, (message) =>
        message.id === messageId && message.role === 'assistant' ? { ...message, status } : message,
    );
